import { z } from 'zod'
import interceptedAxios from '../../../utils/interceptedAxios'

/**
 * @fileoverview Service for fetching dynamic frequency metadata from the asset-config-service
 * This service provides frequency options for both Modbus parameters and KPI configurations
 * @author TIQ Development Team
 * @version 1.0.0
 */

// Zod schemas for runtime type validation
const FrequencyOptionSchema = z.object({
  label: z.string(),
  value: z.string()
})

const FrequencyDataSchema = z.object({
  modbusFrequencies: z.array(FrequencyOptionSchema),
  kpiFrequencies: z.array(FrequencyOptionSchema)
})

const FrequencyApiResponseSchema = z.object({
  http_status: z.number(),
  error: z.boolean(),
  data: FrequencyDataSchema
})

// TypeScript types derived from Zod schemas
export type FrequencyOption = z.infer<typeof FrequencyOptionSchema>
export type FrequencyData = z.infer<typeof FrequencyDataSchema>
export type FrequencyApiResponse = z.infer<typeof FrequencyApiResponseSchema>

/**
 * Fetches frequency metadata from the asset-config-service API
 * @param onSuccess - Callback function called on successful API response
 * @param onFailure - Callback function called on API failure or error
 * @returns Promise<void>
 */

export async function fetchFrequencyMetaData (
  onSuccess: (data: FrequencyData, httpStatus: number) => void,
  onFailure: (errorMessage: string, httpStatus: number) => void
): Promise<void> {
  const url = '/asset-config-service/api/v1/udp/tiq/commands/configuration/device-template/metadata/execution-frequency'

  try {
    const response = await interceptedAxios.get(url, { timeout: 15000 })
    const responseData = response.data

    if (responseData.error === false) {
      if (responseData.data != null) {
        // Validate response structure using Zod schema
        const parseResult = FrequencyApiResponseSchema.safeParse(responseData)
        if (parseResult.success) {
          onSuccess(parseResult.data.data, parseResult.data.http_status)
        } else {
          onFailure('Invalid response structure from frequency metadata API', response.status)
        }
      } else {
        onFailure('No frequency data received from API', responseData.http_status ?? response.status)
      }
    } else {
      onFailure(responseData.error_message ?? 'API returned error status', response.status)
    }
  } catch (error: any) {
    // Handle network errors and API failures
    if (error.response != null) {
      const errorMessage = error.response.data?.error_message ?? 'Network Error'
      onFailure(errorMessage, error.response.status)
    } else {
      onFailure('Network connection failed', 500)
    }
  }
}
